import { motion } from 'framer-motion'
import { icons } from "../assets/icons/icons";
import { useSound } from "../components/SoundHandler";
import { ContainerShape, HexagonShape, InnerShape, InnerShape2 } from "../components/Shape";
import { useResolution } from "../hooks/useResolution";
import { useReferralCode, useReferralStats } from "../hooks/useReferral";
import { useToast } from "~/core/contexts/ToastContext";
import { WWW } from "~/core/constants";

export const ReferralModal = ({ onDismiss }: { onDismiss: () => void }) => {
  const { data: referralCodeData } = useReferralCode()
  const { data: referralStats } = useReferralStats()
  const toast = useToast()
  const sounds = useSound()

   const onClick = () => {
    if (!referralCodeData?.referralCode) return
    const copyText = `${WWW}/?ref=${referralCodeData.referralCode}`;
    navigator.clipboard.writeText(copyText);
    toast.success('Copied to clipboard')
    sounds.play('click');
  }

  const onClose = () => {
    onDismiss()
    sounds.play('click')
  };

  const { isMobile } = useResolution() 

  const honeyEarned = referralStats?.totalReferralHoney ?? '0'

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <motion.div
        className="absolute inset-0 bg-[#0a0523]/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
      />

      <div className='flex flex-col md:w-[500px] mx-auto h-full md:h-auto'>
        <div className='flex-1 overflow-hidden'>
          <ContainerShape {...(
            isMobile 
              ? {cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 } 
              : {cornerDiagonal: 60, cornerRadius: 25, strokeWidth: 2 })
            }>
            <div className="flex flex-col items-center p-6 md:p-8">
              <div className="mt-1 text-[48px] font-blinko font-black text-white">
                Referrals
              </div>
              <p className="flex gap-1 text-sm text-white/70 mt-3">
                Get <span className="text-white">10%</span> of each invited friend's total honey.
              </p>

              <div className="relative mt-8">
                <SvgComponent />
                <div className="absolute inset-0 pt-6 justify-center items-center flex flex-col gap-3 font-blinko">
                  <p className="text-[32px] text-white">{honeyEarned}</p>
                  <p className="text-[#FFFFFFCC] text-[14px]">HONEY EARNED</p>
                </div>
              </div>

              <div className='mt-8'>
                <p className="text-white text-center font-blinko text-[10px]">SHARE YOUR CODE</p>
                <div className="w-[350px] mt-1.5">
                  <InnerShape2 cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                    <div
                      className="flex flex-1 w-full h-10 p-1 bg-transparent text-sm text-white"
                    >
                      <div className="flex-1 px-2 py-1.5">
                        {referralCodeData?.referralCode}
                      </div>

                      <button onClick={onClick}>
                        <InnerShape cornerDiagonal={10} cornerRadius={6} strokeWidth={1}>
                          <p className="px-3 py-2 text-xs font-blinko text-white">Copy</p>
                        </InnerShape>
                      </button>
                    </div>
                  </InnerShape2>
                </div>
              </div>

              <div className="mt-20 flex justify-end space-x-2">
                <button onClick={onClose}>
                  <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                    <div className="relative px-6 py-3">
                      <p 
                        className="text-sm font-blinko text-white"
                      >
                        BACK TO GAME
                      </p>
                    </div>
                  </InnerShape>
                </button>
              </div>
            </div>
          </ContainerShape>
        </div>
      </div>
      
    </div>
  );
}



const SvgComponent = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={210}
    height={210}
    fill="none"
    {...props}
  >
    <foreignObject width={205.865} height={229.05} x={2.067} y={-9.525}>
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          backdropFilter: "blur(6px)",
          clipPath: "url(#a)",
          height: "100%",
          width: "100%",
        }}
      />
    </foreignObject>
    <g data-figma-bg-blur-radius={12}>
      <path
        fill="#F0B100"
        fillOpacity={0.15}
        d="M97 4.619a16 16 0 0 1 16 0l74.933 43.262a16 16 0 0 1 8 13.857v86.524a16 16 0 0 1-8 13.857L113 205.381a16 16 0 0 1-16 0l-74.933-43.262a16 16 0 0 1-8-13.857V61.738a16 16 0 0 1 8-13.857L97 4.62Z"
      />
      <path
        stroke="url(#b)"
        strokeOpacity={0.25}
        strokeWidth={2}
        d="M97.5 5.484a15.001 15.001 0 0 1 14.562-.243l.438.243 74.933 43.263a15 15 0 0 1 7.5 12.99v86.526a15 15 0 0 1-7.5 12.99L112.5 204.516a15.002 15.002 0 0 1-15 0l-74.933-43.263a15 15 0 0 1-7.5-12.99V61.737a15 15 0 0 1 7.5-12.99L97.5 5.484Z"
      />
    </g>
    <foreignObject width={79.426} height={85.525} x={65.787} y={1.238}>
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          backdropFilter: "blur(6px)",
          clipPath: "url(#c)",
          height: "100%",
          width: "100%",
        }}
      />
    </foreignObject>
    <path
      fill="#F0B100"
      fillOpacity={0.4}
      stroke="url(#d)"
      d="M101.75 14.742a7.5 7.5 0 0 1 7.281-.12l.219.12 19.713 11.382a7.501 7.501 0 0 1 3.75 6.495v22.762c0 2.68-1.43 5.155-3.75 6.495L109.25 73.258a7.5 7.5 0 0 1-7.5 0L82.037 61.876a7.5 7.5 0 0 1-3.75-6.495V32.619a7.5 7.5 0 0 1 3.75-6.495l19.713-11.382Z"
      data-figma-bg-blur-radius={12}
    />
    <path
      fill="#fff"
      d="M97.5 32.667c0-.622 0-.932.102-1.177.135-.327.394-.587.721-.722.245-.101.556-.101 1.177-.101h12c.621 0 .932 0 1.177.101.327.135.586.395.722.722.101.245.101.555.101 1.177 0 .62 0 .931-.101 1.176a1.335 1.335 0 0 1-.722.722c-.245.102-.556.102-1.177.102h-12c-.621 0-.932 0-1.177-.102a1.333 1.333 0 0 1-.721-.721c-.102-.245-.102-.556-.102-1.177Z"
    />
    <path
      stroke="#fff"
      strokeLinecap="round"
      strokeWidth={1.5}
      d="M95.5 52h20M95.5 41.333h20M96.835 38.399l3.204-2.564c.723-.578 1.085-.868 1.514-1.018.428-.15.891-.15 1.818-.15h4.319c.935 0 1.402 0 1.834.152.432.153.795.447 1.522 1.036l3.143 2.544a5.334 5.334 0 0 1 1.978 4.145v8.876a5.33 5.33 0 0 1-1.217 3.39l-.478.58a5.332 5.332 0 0 1-4.116 1.943h-8.914c-.761 0-1.142 0-1.503-.05a5.333 5.333 0 0 1-2.87-1.343c-.268-.246-.512-.538-1-1.123a5.334 5.334 0 0 1-1.236-3.415v-8.839c0-1.62.737-3.152 2.002-4.164Z"
    />
    <defs>
      <linearGradient
        id="b"
        x1={210}
        x2={0}
        y1={0}
        y2={210}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFF085" />
        <stop offset={1} stopColor="#FFE10C" />
      </linearGradient>
      <linearGradient
        id="d"
        x1={137.5}
        x2={73.5}
        y1={12}
        y2={76}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FFF085" />
        <stop offset={1} stopColor="#FFE10C" />
      </linearGradient>
      <clipPath id="a" transform="translate(-2.067 9.525)">
        <path d="M97 4.619a16 16 0 0 1 16 0l74.933 43.262a16 16 0 0 1 8 13.857v86.524a16 16 0 0 1-8 13.857L113 205.381a16 16 0 0 1-16 0l-74.933-43.262a16 16 0 0 1-8-13.857V61.738a16 16 0 0 1 8-13.857L97 4.62Z" />
      </clipPath>
      <clipPath id="c" transform="translate(-65.787 -1.238)">
        <path d="M101.75 14.742a7.5 7.5 0 0 1 7.281-.12l.219.12 19.713 11.382a7.501 7.501 0 0 1 3.75 6.495v22.762c0 2.68-1.43 5.155-3.75 6.495L109.25 73.258a7.5 7.5 0 0 1-7.5 0L82.037 61.876a7.5 7.5 0 0 1-3.75-6.495V32.619a7.5 7.5 0 0 1 3.75-6.495l19.713-11.382Z" />
      </clipPath>
    </defs>
  </svg>
)
